import clsx from 'clsx'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { MattersActions, MatterDialogType } from '@/matter/store/slice'
import { selectInteractiveMattersSortedByName } from '@/matter/store/selectors'
import { RootState } from '@/store/store'
import { GlobalToastType } from '@/constants/constants-ui'
import { MatterSelectDropdown } from '@/components/matter-select'
import type { MatterRead } from '@/matter/store/schemas'

// ---------------------------------------------------------------------------
// Presentational: pure UI, no Redux/analytics. Easy to use in Storybook.
// ---------------------------------------------------------------------------

export type SelectActiveMatterPanelViewProps = {
  title: string
  description: string
  variant?: 'standard' | 'dashboard'
  matters: MatterRead[]
  activeMatterId: string | null
  onSelect: (matterId: string) => void
  onCreateNewMatter: () => void
}

export function SelectActiveMatterPanelView({
  title,
  description,
  variant = 'standard',
  matters,
  activeMatterId,
  onSelect,
  onCreateNewMatter,
}: SelectActiveMatterPanelViewProps) {
  return (
    <div data-testid="select-matter-panel" className="flex flex-grow p-6 lg:p-10 overflow-y-auto">
      <div className="w-full max-w-5xl m-auto">
        <h2 className={clsx('font-semibold text-center mb-2 text-brand-neutral-900', variant === 'dashboard' ? 'text-3xl' : 'text-lg')}>{title}</h2>
        <p className={clsx('text-center text-brand-neutral-700 font-normal mb-6', variant === 'dashboard' ? 'text-base' : 'text-sm')}>{description}</p>
        <div className="w-full max-w-[412px] mx-auto">
          <MatterSelectDropdown
            arrowIcon="ChevronDown"
            emptyStateMessage="Create a matter for full access to all features"
            fullWidthContent
            items={matters}
            onCreateNewMatter={onCreateNewMatter}
            onSelect={onSelect}
            placeholder="Select Matter"
            selectedItemId={activeMatterId}
            size="large"
            variant="field"
          />
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Container: wires Redux to the view (matter selection analytics live in the matters listener).
// ---------------------------------------------------------------------------

type SelectActiveMatterPanelProps = {
  title: string
  description: string
  variant?: 'standard' | 'dashboard'
  /** Limits the pickable matters, e.g. to those where the host feature is available to the user. */
  canSelectMatter?: (matter: MatterRead) => boolean
}

export default function SelectActiveMatterPanel({ title, description, variant = 'standard', canSelectMatter }: SelectActiveMatterPanelProps) {
  const dispatch = useAppDispatch()

  const allMatters = useAppSelector(selectInteractiveMattersSortedByName)
  const matters = canSelectMatter ? allMatters.filter(canSelectMatter) : allMatters
  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)

  const onMatterSelect = (matterId: string) => {
    if (matterId === activeMatterId) return

    dispatch(MattersActions.setActiveMatterId({ matterId }))

    const matterName = matters.find((m) => m.id === matterId)?.name ?? 'Untitled Matter'
    if (matterName) {
      dispatch(
        openGlobalToast({
          type: GlobalToastType.SUCCESS,
          message: `Switched to Matter: ${matterName}!`,
          durationMs: 2000,
          truncateAtLine: 3,
        })
      )
    }
  }

  return (
    <SelectActiveMatterPanelView
      activeMatterId={activeMatterId}
      matters={matters}
      onCreateNewMatter={() => dispatch(MattersActions.openDialog({ dialogType: MatterDialogType.Create, switchToMatterOnCreate: true }))}
      onSelect={onMatterSelect}
      description={description}
      title={title}
      variant={variant}
    />
  )
}
